import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SceneObject {
  id: string;
  type: 'wall' | 'floor' | 'ceiling' | 'pillar' | 'equipment' | 'rack' | 'table' | 'room' | 'device' | 'sensor';
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  scale: { x: number; y: number; z: number };
  size?: { width: number; height: number; depth: number };
  color?: string;
  opacity?: number;
  material?: 'basic' | 'standard' | 'glass' | 'metal' | 'concrete';
  geometry?: 'box' | 'cylinder' | 'sphere' | 'plane';
  text?: string;
  texture?: string;
  visible?: boolean;
  attachedDevices?: string[];
}

export interface DevicePlacement {
  id: string;
  deviceId: string;
  deviceName?: string;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  scale: { x: number; y: number; z: number };
  sensors: SensorVisual[];
  isSelected?: boolean;
}

export interface SensorVisual {
  id: string;
  sensorId: string;
  sensorName?: string;
  sensorType?: string;
  type: 'gauge' | 'text' | 'heatmap' | 'chart' | 'billboard';
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  scale: { x: number; y: number; z: number };
  size: { width: number; height: number };
  config: {
    showValue?: boolean;
    showUnit?: boolean;
    showLabel?: boolean;
    color?: string;
    alwaysFaceCamera?: boolean;
    threshold?: {
      min?: number;
      max?: number;
      warningColor?: string;
      alertColor?: string;
    };
  };
  currentValue?: number;
  unit?: string;
  isSelected?: boolean;
}

export interface Scene {
  id: string;
  name: string;
  description?: string;
  locationId: string;
  locationName: string;
  deviceIds: string[];
  objects: SceneObject[];
  devicePlacements: DevicePlacement[];
  thumbnail?: string;
  createdAt: string;
  updatedAt: string;
  settings: {
    gridSize: number;
    snapToGrid: boolean;
    backgroundColor: string;
    dimensions: { width: number; height: number; depth: number };
    lighting: {
      ambientIntensity: number;
      directionalIntensity: number;
      directionalPosition: { x: number; y: number; z: number };
    };
    camera: {
      position: { x: number; y: number; z: number };
      target: { x: number; y: number; z: number };
      fov: number;
    };
    physics: {
      enabled: boolean;
      gravity: { x: number; y: number; z: number };
    };
  };
}

interface SceneStore {
  scenes: Scene[];
  currentScene: Scene | null;
  selectedObject: string | null;
  gridVisible: boolean;
  snapToGrid: boolean;

  // Scene management
  addScene: (scene: Omit<Scene, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateScene: (id: string, updates: Partial<Scene>) => void;
  deleteScene: (id: string) => void;
  setCurrentScene: (scene: Scene | null) => void;
  duplicateScene: (id: string) => void;

  // Object management
  addObject: (sceneId: string, object: Omit<SceneObject, 'id'>) => void;
  updateObject: (sceneId: string, objectId: string, updates: Partial<SceneObject>) => void;
  deleteObject: (sceneId: string, objectId: string) => void;
  setSelectedObject: (objectId: string | null) => void;

  // Device placement
  addDevicePlacement: (sceneId: string, placement: Omit<DevicePlacement, 'id'>) => void;
  updateDevicePlacement: (sceneId: string, placementId: string, updates: Partial<DevicePlacement>) => void;
  deleteDevicePlacement: (sceneId: string, placementId: string) => void;

  // Sensor visual management
  addSensorVisual: (sceneId: string, placementId: string, visual: Omit<SensorVisual, 'id'>) => void;
  updateSensorVisual: (sceneId: string, placementId: string, visualId: string, updates: Partial<SensorVisual>) => void;
  deleteSensorVisual: (sceneId: string, placementId: string, visualId: string) => void;

  // Editor settings
  setGridVisible: (visible: boolean) => void;
  setSnapToGrid: (snap: boolean) => void;

  // Utility functions
  generateThumbnail: (sceneId: string, dataUrl: string) => void;
}

const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36);

// Helper functions to reduce nesting
const updateSceneInArray = (scenes: Scene[], sceneId: string, updater: (scene: Scene) => Scene) => {
  return scenes.map((scene) => (scene.id === sceneId ? updater(scene) : scene));
};

const updateCurrentScene = (currentScene: Scene | null, sceneId: string, updater: (scene: Scene) => Scene) => {
  return currentScene?.id === sceneId ? updater(currentScene) : currentScene;
};

export const useSceneStore = create<SceneStore>()(
  persist(
    (set, get) => ({
      scenes: [],
      currentScene: null,
      selectedObject: null,
      gridVisible: true,
      snapToGrid: true,

      addScene: (sceneData) => {
        const newScene: Scene = {
          ...sceneData,
          id: generateId(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        set((state) => ({
          scenes: [...state.scenes, newScene],
        }));
      },

      updateScene: (id, updates) => {
        const timestamp = new Date().toISOString();
        set((state) => ({
          scenes: state.scenes.map((scene) =>
            scene.id === id ? { ...scene, ...updates, updatedAt: timestamp } : scene,
          ),
          currentScene:
            state.currentScene?.id === id
              ? { ...state.currentScene, ...updates, updatedAt: timestamp }
              : state.currentScene,
        }));
      },

      deleteScene: (id) => {
        set((state) => ({
          scenes: state.scenes.filter((scene) => scene.id !== id),
          currentScene: state.currentScene?.id === id ? null : state.currentScene,
        }));
      },

      setCurrentScene: (scene) => {
        set({ currentScene: scene });
      },

      duplicateScene: (id) => {
        const scene = get().scenes.find((s) => s.id === id);
        if (scene) {
          const duplicatedScene: Scene = {
            ...scene,
            id: generateId(),
            name: `${scene.name} (Copy)`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          set((state) => ({
            scenes: [...state.scenes, duplicatedScene],
          }));
        }
      },

      addObject: (sceneId, objectData) => {
        const object: SceneObject = { ...objectData, id: generateId() };
        const timestamp = new Date().toISOString();

        set((state) => ({
          scenes: updateSceneInArray(state.scenes, sceneId, (scene) => ({
            ...scene,
            objects: [...scene.objects, object],
            updatedAt: timestamp,
          })),
          currentScene: updateCurrentScene(state.currentScene, sceneId, (scene) => ({
            ...scene,
            objects: [...scene.objects, object],
            updatedAt: timestamp,
          })),
        }));
      },

      updateObject: (sceneId, objectId, updates) => {
        const timestamp = new Date().toISOString();
        const updateObjects = (objects: SceneObject[]) =>
          objects.map((obj) => (obj.id === objectId ? { ...obj, ...updates } : obj));

        set((state) => ({
          scenes: updateSceneInArray(state.scenes, sceneId, (scene) => ({
            ...scene,
            objects: updateObjects(scene.objects),
            updatedAt: timestamp,
          })),
          currentScene: updateCurrentScene(state.currentScene, sceneId, (scene) => ({
            ...scene,
            objects: updateObjects(scene.objects),
            updatedAt: timestamp,
          })),
        }));
      },

      deleteObject: (sceneId, objectId) => {
        const timestamp = new Date().toISOString();
        const filterObjects = (objects: SceneObject[]) => objects.filter((obj) => obj.id !== objectId);

        set((state) => ({
          scenes: updateSceneInArray(state.scenes, sceneId, (scene) => ({
            ...scene,
            objects: filterObjects(scene.objects),
            updatedAt: timestamp,
          })),
          currentScene: updateCurrentScene(state.currentScene, sceneId, (scene) => ({
            ...scene,
            objects: filterObjects(scene.objects),
            updatedAt: timestamp,
          })),
          selectedObject: state.selectedObject === objectId ? null : state.selectedObject,
        }));
      },

      setSelectedObject: (objectId) => {
        set({ selectedObject: objectId });
      },

      addDevicePlacement: (sceneId, placementData) => {
        const placement: DevicePlacement = { ...placementData, id: generateId() };
        const timestamp = new Date().toISOString();

        set((state) => ({
          scenes: updateSceneInArray(state.scenes, sceneId, (scene) => ({
            ...scene,
            devicePlacements: [...scene.devicePlacements, placement],
            updatedAt: timestamp,
          })),
          currentScene: updateCurrentScene(state.currentScene, sceneId, (scene) => ({
            ...scene,
            devicePlacements: [...scene.devicePlacements, placement],
            updatedAt: timestamp,
          })),
        }));
      },

      updateDevicePlacement: (sceneId, placementId, updates) => {
        const timestamp = new Date().toISOString();
        const updatePlacements = (placements: DevicePlacement[]) =>
          placements.map((p) => (p.id === placementId ? { ...p, ...updates } : p));

        set((state) => ({
          scenes: updateSceneInArray(state.scenes, sceneId, (scene) => ({
            ...scene,
            devicePlacements: updatePlacements(scene.devicePlacements),
            updatedAt: timestamp,
          })),
          currentScene: updateCurrentScene(state.currentScene, sceneId, (scene) => ({
            ...scene,
            devicePlacements: updatePlacements(scene.devicePlacements),
            updatedAt: timestamp,
          })),
        }));
      },

      deleteDevicePlacement: (sceneId, placementId) => {
        const timestamp = new Date().toISOString();
        const filterPlacements = (placements: DevicePlacement[]) => placements.filter((p) => p.id !== placementId);

        set((state) => ({
          scenes: updateSceneInArray(state.scenes, sceneId, (scene) => ({
            ...scene,
            devicePlacements: filterPlacements(scene.devicePlacements),
            updatedAt: timestamp,
          })),
          currentScene: updateCurrentScene(state.currentScene, sceneId, (scene) => ({
            ...scene,
            devicePlacements: filterPlacements(scene.devicePlacements),
            updatedAt: timestamp,
          })),
        }));
      },

      addSensorVisual: (sceneId, placementId, visualData) => {
        const visual: SensorVisual = { ...visualData, id: generateId() };
        const timestamp = new Date().toISOString();
        const updatePlacementSensors = (placements: DevicePlacement[]) =>
          placements.map((p) => (p.id === placementId ? { ...p, sensors: [...p.sensors, visual] } : p));

        set((state) => ({
          scenes: updateSceneInArray(state.scenes, sceneId, (scene) => ({
            ...scene,
            devicePlacements: updatePlacementSensors(scene.devicePlacements),
            updatedAt: timestamp,
          })),
          currentScene: updateCurrentScene(state.currentScene, sceneId, (scene) => ({
            ...scene,
            devicePlacements: updatePlacementSensors(scene.devicePlacements),
            updatedAt: timestamp,
          })),
        }));
      },

      updateSensorVisual: (sceneId, placementId, visualId, updates) => {
        const timestamp = new Date().toISOString();
        const updateSensorInPlacement = (sensors: SensorVisual[]) =>
          sensors.map((v) => (v.id === visualId ? { ...v, ...updates } : v));
        const updatePlacementVisuals = (placements: DevicePlacement[]) =>
          placements.map((p) => (p.id === placementId ? { ...p, sensors: updateSensorInPlacement(p.sensors) } : p));

        set((state) => ({
          scenes: updateSceneInArray(state.scenes, sceneId, (scene) => ({
            ...scene,
            devicePlacements: updatePlacementVisuals(scene.devicePlacements),
            updatedAt: timestamp,
          })),
          currentScene: updateCurrentScene(state.currentScene, sceneId, (scene) => ({
            ...scene,
            devicePlacements: updatePlacementVisuals(scene.devicePlacements),
            updatedAt: timestamp,
          })),
        }));
      },

      deleteSensorVisual: (sceneId, placementId, visualId) => {
        const timestamp = new Date().toISOString();
        const filterSensorsInPlacement = (sensors: SensorVisual[]) => sensors.filter((v) => v.id !== visualId);
        const filterPlacementVisuals = (placements: DevicePlacement[]) =>
          placements.map((p) => (p.id === placementId ? { ...p, sensors: filterSensorsInPlacement(p.sensors) } : p));

        set((state) => ({
          scenes: updateSceneInArray(state.scenes, sceneId, (scene) => ({
            ...scene,
            devicePlacements: filterPlacementVisuals(scene.devicePlacements),
            updatedAt: timestamp,
          })),
          currentScene: updateCurrentScene(state.currentScene, sceneId, (scene) => ({
            ...scene,
            devicePlacements: filterPlacementVisuals(scene.devicePlacements),
            updatedAt: timestamp,
          })),
        }));
      },

      setGridVisible: (visible) => {
        set({ gridVisible: visible });
      },

      setSnapToGrid: (snap) => {
        set({ snapToGrid: snap });
      },

      generateThumbnail: (sceneId, dataUrl) => {
        const timestamp = new Date().toISOString();
        set((state) => ({
          scenes: state.scenes.map((scene) =>
            scene.id === sceneId ? { ...scene, thumbnail: dataUrl, updatedAt: timestamp } : scene,
          ),
        }));
      },
    }),
    {
      name: 'scene-store',
      partialize: (state) => ({
        scenes: state.scenes,
      }),
    },
  ),
);
